/**
 * TierConfigPanel — Phase T2 (Customer Tier perks engine).
 *
 * Super-admin editor for thresholds + per-tier perks. Reads/writes
 *   GET  /api/admin/tier-config
 *   PUT  /api/admin/tier-config
 *
 * Designed to live inside `SettingsPage` next to the feature flags and
 * Sentry triage. Aesthetic matches the dispatch terminal vibe (mono
 * labels, neon green active state, sharp borders).
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Award, RefreshCw, Save, Sparkles, Crown, Leaf } from 'lucide-react';
import { TierPerksEditor } from './TierPerksEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { key: 'new',     label: 'SILVER',   color: '#C0C0C0', icon: Leaf },
  { key: 'regular', label: 'GOLD',     color: '#FFD700', icon: Award },
  { key: 'vip',     label: 'PLATINUM', color: '#00F0FF', icon: Sparkles },
  { key: 'whale',   label: 'ONYX',     color: '#BF5AF2', icon: Crown },
];


function NumberCell({ value, onChange, suffix, placeholder, color, testId, step = '1' }) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        step={step}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="w-24 bg-[#0C0612] border border-[#2A0F3D] focus:border-[#39FF14] font-mono text-sm px-2 py-1 text-white outline-none rounded-none"
        style={color ? { borderColor: color + '66', color } : undefined}
        data-testid={testId}
      />
      {suffix && <span className="font-mono text-[10px] text-[#6B8E73]">{suffix}</span>}
    </div>
  );
}


export function TierConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchCfg = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/tier-config`, { withCredentials: true });
      setCfg(data);
      setDraft(JSON.parse(JSON.stringify(data)));
    } catch (e) {
      toast.error(`Failed to load tier config: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        thresholds: draft.thresholds,
        earn_multiplier_by_tier: draft.earn_multiplier_by_tier,
        free_delivery_threshold_by_tier: draft.free_delivery_threshold_by_tier,
        birthday_bonus_points_by_tier: draft.birthday_bonus_points_by_tier,
        lock_days: draft.lock_days,
      };
      const { data } = await axios.put(`${API_URL}/api/admin/tier-config`, payload, { withCredentials: true });
      setCfg(data);
      setDraft(JSON.parse(JSON.stringify(data)));
      toast.success('Tier config saved');
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);

  if (!draft) {
    return (
      <div className="border border-[#2A0F3D] bg-[#0A0008] p-4 mb-6 text-[#A3C7A8] font-mono text-sm">
        {loading ? <><span className="cursor-blink">█</span> Loading tier config...</> : 'Tier config unavailable.'}
      </div>
    );
  }

  return (
    <div className="border border-[#FF9F0A]/60 bg-[#0A0008] mb-6" data-testid="tier-config-panel">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-[#FF9F0A]/40 bg-gradient-to-r from-[#0A0008] to-[#1A1129]">
        <Award className="w-5 h-5 text-[#FF9F0A]" />
        <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-[#FF9F0A]">Customer Tiers &middot; T2 Perks Engine</h2>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={fetchCfg}
            disabled={loading}
            className="px-2 py-1 border border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] font-mono text-xs rounded-none disabled:opacity-50"
            data-testid="tier-config-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="px-3 py-1 border bg-[#39FF14]/10 border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/20 font-mono text-xs uppercase tracking-wider rounded-none disabled:opacity-40 disabled:hover:bg-[#39FF14]/10 flex items-center gap-1"
            data-testid="tier-config-save-btn"
          >
            <Save className="w-3 h-3" />
            {saving ? 'Saving...' : (dirty ? 'Save Changes' : 'Saved')}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* Per-tier table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-[#6B8E73]">
                <th className="py-2 pr-4 w-24">TIER</th>
                <th className="py-2 pr-4 w-40" title="Lifetime spend required to enter this tier">SPEND ≥</th>
                <th className="py-2 pr-4" title="Points earn multiplier on delivered orders">EARN ×</th>
                <th className="py-2 pr-4" title="Subtotal at/above which delivery fee waives">FREE DEL ≥</th>
                <th className="py-2 pr-4" title="Annual birthday bonus points">BIRTHDAY PTS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A0F3D]">
              {TIERS.map(({ key, label, color, icon: Icon }) => (
                <tr key={key} data-testid={`tier-config-row-${key}`}>
                  <td className="py-3 pr-4">
                    <div
                      className="inline-flex items-center gap-2 px-2 py-1 border font-mono text-xs uppercase tracking-wider"
                      style={{ borderColor: color, color }}
                    >
                      <Icon className="w-3 h-3" /> {label}
                    </div>
                  </td>
                  <td className="py-3 pr-4">
                    {key === 'new' ? (
                      <span className="font-mono text-xs text-[#6B8E73]">$0 &mdash; entry tier</span>
                    ) : (
                      <NumberCell
                        value={draft.thresholds?.[key]}
                        suffix="USD"
                        color={color}
                        testId={`tier-threshold-${key}`}
                        onChange={(v) => setDraft({
                          ...draft,
                          thresholds: { ...draft.thresholds, [key]: v ?? 0 },
                        })}
                      />
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <NumberCell
                      value={draft.earn_multiplier_by_tier?.[key]}
                      suffix="x"
                      color={color}
                      step="0.05"
                      testId={`tier-earn-mult-${key}`}
                      onChange={(v) => setDraft({
                        ...draft,
                        earn_multiplier_by_tier: { ...draft.earn_multiplier_by_tier, [key]: v ?? 1 },
                      })}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <NumberCell
                      value={draft.free_delivery_threshold_by_tier?.[key]}
                      suffix="USD"
                      placeholder="never"
                      color={color}
                      testId={`tier-free-del-${key}`}
                      onChange={(v) => setDraft({
                        ...draft,
                        free_delivery_threshold_by_tier: { ...draft.free_delivery_threshold_by_tier, [key]: v },
                      })}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <NumberCell
                      value={draft.birthday_bonus_points_by_tier?.[key]}
                      suffix="pts"
                      color={color}
                      testId={`tier-birthday-${key}`}
                      onChange={(v) => setDraft({
                        ...draft,
                        birthday_bonus_points_by_tier: { ...draft.birthday_bonus_points_by_tier, [key]: v ?? 0 },
                      })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Lock-in policy */}
        <div className="flex items-center gap-4 pt-2 border-t border-[#2A0F3D]">
          <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#6B8E73]">PROMOTION LOCK</label>
          <NumberCell
            value={draft.lock_days}
            suffix="DAYS"
            testId="tier-lock-days"
            onChange={(v) => setDraft({ ...draft, lock_days: v ?? 90 })}
          />
          <p className="text-[#A3C7A8] text-xs font-mono">
            Customers cannot drop below a promoted tier for this many days &mdash; prevents a brief pause from demoting an ONYX customer.
          </p>
        </div>

        <TierPerksEditor />

        {/* Help footer */}
        <div className="text-xs font-mono text-[#6B8E73] leading-relaxed border-t border-[#2A0F3D] pt-3">
          // EARN ×  applies the multiplier AFTER the base loyalty earn rate (configured in Loyalty settings). Daily cap still applies.<br/>
          // FREE DEL  &mdash; subtotal at/above which delivery fee waives. Leave blank for &ldquo;never&rdquo;.<br/>
          // BIRTHDAY PTS &mdash; awarded annually on the customer&rsquo;s birthday (requires birthday field on profile; auto-skipped if missing).
        </div>
      </div>
    </div>
  );
}

export default TierConfigPanel;
